import React, { useCallback, useEffect, useState } from "react";
import {
  listPendingPlatformOrgInvitations,
  resendPlatformOrgInvitation,
  sendPlatformOrgInvitation,
} from "../../api/api";
import {
  NotificationTabHeader,
  NotificationEmptyState,
  NotificationErrorPanel,
  NotificationRequestCard,
} from "../notifications_page/NotificationTabShared";
import { Button } from "../ui/button";

const fieldClass =
  "w-full rounded-md border border-ui-border bg-ui-card px-3 py-2 text-sm text-text-primary placeholder:text-text-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ui-ring focus-visible:ring-offset-2 focus-visible:ring-offset-ui-background";

/**
 * Platform outreach: invite pastors to create an organization via /new-org.
 * @param {{ adminId: number }} props
 * @returns {JSX.Element}
 */
function PlatformOrgInvitationsTab({ adminId }) {
  const [inviteeName, setInviteeName] = useState("");
  const [email, setEmail] = useState("");
  const [items, setItems] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [error, setError] = useState("");
  const [sending, setSending] = useState(false);
  const [resendingId, setResendingId] = useState(0);

  const loadPending = useCallback(async () => {
    if (!adminId) return;
    setLoadingList(true);
    setError("");
    const result = await listPendingPlatformOrgInvitations(adminId);
    if (result.error) {
      setError(result.error);
      setItems([]);
    } else {
      setItems(Array.isArray(result.data) ? result.data : []);
    }
    setLoadingList(false);
  }, [adminId]);

  useEffect(() => {
    loadPending();
  }, [loadPending]);

  /**
   * @returns {Promise<void>}
   */
  async function onSend() {
    setSending(true);
    setError("");
    const result = await sendPlatformOrgInvitation(adminId, {
      email: email.trim(),
      invitee_name: inviteeName.trim(),
    });
    setSending(false);
    if (result.error) {
      setError(result.error);
      return;
    }
    setEmail("");
    setInviteeName("");
    await loadPending();
  }

  /**
   * @param {number} invitationId
   * @returns {Promise<void>}
   */
  async function onResend(invitationId) {
    setResendingId(invitationId);
    setError("");
    const result = await resendPlatformOrgInvitation(adminId, invitationId);
    setResendingId(0);
    if (result.error) {
      setError(result.error);
      return;
    }
    await loadPending();
  }

  return (
    <div>
      <NotificationTabHeader
        title="Organization invitations"
        description="Invite ministry leaders to create an organization on Assembly. Messages link to the public create-organization flow."
      />

      {error ? <NotificationErrorPanel message={error} /> : null}

      <div className="mb-6 space-y-3 rounded-lg border border-ui-border bg-ui-card p-4">
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label htmlFor="invitee-name" className="mb-1 block text-sm font-medium text-text-primary">
              Addressee name
            </label>
            <input
              id="invitee-name"
              type="text"
              autoComplete="name"
              className={fieldClass}
              placeholder="Pastor Jane"
              value={inviteeName}
              onChange={(e) => setInviteeName(e.target.value)}
            />
          </div>
          <div>
            <label htmlFor="invitee-email" className="mb-1 block text-sm font-medium text-text-primary">
              Email
            </label>
            <input
              id="invitee-email"
              type="email"
              autoComplete="email"
              className={fieldClass}
              placeholder="leader@church.org"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
        </div>
        <Button
          type="button"
          onClick={() => onSend()}
          disabled={
            sending || !inviteeName.trim() || !email.trim() || !adminId
          }
        >
          {sending ? "Sending…" : "Send invitation"}
        </Button>
      </div>

      {loadingList ? (
        <NotificationEmptyState title="Loading invitations…" className="border-solid bg-ui-card" />
      ) : null}

      {!loadingList && items.length === 0 ? (
        <NotificationEmptyState
          title="No pending invitations"
          subtitle="Pending rows appear here until someone creates a user or organization with the same email."
          className="border-solid bg-ui-card"
        />
      ) : null}

      {!loadingList && items.length > 0 ? (
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-text-primary">Pending</h3>
          {items.map((row) => (
            <NotificationRequestCard key={row.id}>
              <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0">
                  <h4 className="truncate text-lg font-semibold text-text-primary">{row.invitee_name}</h4>
                  <p className="truncate text-sm text-text-secondary">{row.email}</p>
                  <p className="text-xs text-text-muted">
                    Last sent{" "}
                    {row.last_sent_at
                      ? new Date(row.last_sent_at).toLocaleString(undefined, {
                          dateStyle: "medium",
                          timeStyle: "short",
                        })
                      : "—"}
                  </p>
                </div>
                <div className="flex shrink-0">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => onResend(row.id)}
                    disabled={resendingId === row.id}
                  >
                    {resendingId === row.id ? "Sending…" : "Resend"}
                  </Button>
                </div>
              </div>
            </NotificationRequestCard>
          ))}
        </div>
      ) : null}
    </div>
  );
}

export default PlatformOrgInvitationsTab;
